/** */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTenantService } from 'hooks/api/iam/tenant';
import { useModuleService } from 'hooks/api/sales/module';
import { useSubscriptionService } from 'hooks/api/sales/subscriptions';
import WebDataTable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import GmModal from 'app/shared/modal/modal';
import {
  DAY_MS,
  fetchAllPages,
  mostRecentSubscriptionPerTenant,
  SIX_MONTHS_DAYS
} from 'app/member/modules/billing/subscriptions/utils';
import { fmt } from 'utilities/format';
import { OpportunityDetail } from './detail';

const CORE_MODULE_CODE = 'campaign';
const PRICING_CURRENCY = 'NGN';
const UPGRADE_PATHS = { explorer: 'professional', professional: 'enterprise' };
const TENANT_ID_BATCH_SIZE = 50;

const table_config = {
  actions: {},
  css: {},
  fields: [
    { title: 'Name', key: 'name', isTitle: true, searchable: true },
    {
      title: 'Active Subscription',
      key: 'active_subscriptions',
      isTagline: true,
      formatter: (value) => value?.join(', ') || '—'
    },
    {
      title: 'Monthly Spend',
      key: 'monthly_spend',
      formatter: (value) => fmt(value, 'currency')
    },
    { title: 'Contacts', key: 'contacts_count', formatter: (value) => fmt(value) },
    {
      title: 'Opportunity',
      key: 'opportunity',
      isMetadata: true,
      formatter: (value) => fmt(value, 'currency')
    }
  ],
  search_key: 'name',
  search_text: 'Search tenants'
};

const hasActiveSubscription = (tenant) =>
  Number((tenant.metadata?.active_subscriptions || []).length > 0);

const findBracketPricing = (module, contacts_count) => {
  const pricing = (module?.pricing || []).find(
    ({ contact_bracket = {} }) =>
      contacts_count >= contact_bracket.min && contacts_count <= contact_bracket.max
  );
  const price = pricing?.prices?.find(({ currency }) => currency === PRICING_CURRENCY);
  return price ? { amount: price.amount, contact_bracket: pricing.contact_bracket } : null;
};

const computeReactivationOpportunity = (tenant, modules_by_tier, last_subscription) => {
  if (!last_subscription) return null;

  const { contacts_count = 0 } = tenant.metadata || {};
  const previous_pricing = findBracketPricing(
    modules_by_tier[last_subscription.tier],
    contacts_count
  );
  const last_paid =
    last_subscription.price?.currency?.code === PRICING_CURRENCY
      ? last_subscription.price.amount
      : null;
  const value = previous_pricing ? previous_pricing.amount : last_paid;
  if (value === null || value === undefined) return null;

  return {
    type: 'reactivation',
    previous_tier: last_subscription.tier,
    previous_plan: last_subscription.title || last_subscription.plan_code,
    last_expired_at: last_subscription.expires_at,
    last_paid,
    contact_bracket: previous_pricing?.contact_bracket || null,
    value
  };
};

const computeOpportunity = (tenant, modules_by_tier, last_subscription) => {
  const { active_subscriptions = [], contacts_count = 0 } = tenant.metadata || {};
  if (!active_subscriptions.length) {
    return computeReactivationOpportunity(tenant, modules_by_tier, last_subscription);
  }

  const current_tier = ['professional', 'explorer'].find((tier) =>
    active_subscriptions.includes(tier)
  );
  if (!current_tier) return null;

  const upgrade_tier = UPGRADE_PATHS[current_tier];
  const current_pricing = findBracketPricing(modules_by_tier[current_tier], contacts_count);
  const upgrade_pricing = findBracketPricing(modules_by_tier[upgrade_tier], contacts_count);
  if (!current_pricing || !upgrade_pricing) return null;

  return {
    type: 'upgrade',
    current_tier,
    upgrade_tier,
    contact_bracket: current_pricing.contact_bracket,
    current_price: current_pricing.amount,
    upgrade_price: upgrade_pricing.amount,
    value: upgrade_pricing.amount - current_pricing.amount
  };
};

export const OpportunitiesPage = () => {
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const { fetchTenants } = useTenantService();
  const { fetchModules } = useModuleService();
  const { fetchSubscriptions } = useSubscriptionService();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [is_search_mode, setSearchMode] = useState(false);
  const [search_keyword, setSearchKeyword] = useState('');
  const [page_window, setPageWindow] = useState({ page: 0, population: 10 });
  const [selected_row, setSelectedRow] = useState(null);

  useEffect(() => {
    loadOpportunities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLastSubscriptions = async (tenant_ids) => {
    const batches = [];
    for (let i = 0; i < tenant_ids.length; i += TENANT_ID_BATCH_SIZE) {
      batches.push(tenant_ids.slice(i, i + TENANT_ID_BATCH_SIZE));
    }

    const results = await Promise.all(
      batches.map((batch) =>
        fetchAllPages(async (page, population) => {
          const { subscriptions, size } = await fetchSubscriptions({
            query_string: `plan_code=${CORE_MODULE_CODE}&tenant_id=${batch.join(
              ','
            )}&sort_by=-time_stamp&page=${page}&population=${population}`
          });
          return { items: subscriptions, size };
        })
      )
    );

    return mostRecentSubscriptionPerTenant(results.flat()).reduce(
      (acc, subscription) => ({ ...acc, [subscription.tenant_id]: subscription }),
      {}
    );
  };

  const loadOpportunities = async () => {
    setLoading(true);
    const now = Date.now();
    const six_months_ago = now - SIX_MONTHS_DAYS * DAY_MS;

    const [active_tenants, { modules }] = await Promise.all([
      fetchAllPages(async (page, population) => {
        const { tenants, size } = await fetchTenants({
          query_string: `metadata.last_activity=${six_months_ago}~${now}&sort_by=-metadata.last_activity&page=${page}&population=${population}`
        });
        return { items: tenants, size };
      }),
      fetchModules({ query_string: `code=${CORE_MODULE_CODE}` })
    ]);

    const modules_by_tier = modules.reduce(
      (acc, module) => ({ ...acc, [module.tier]: module }),
      {}
    );
    const external_tenants = active_tenants.filter((tenant) => !tenant.metadata?.is_internal);
    const lapsed_tenant_ids = external_tenants
      .filter((tenant) => !hasActiveSubscription(tenant))
      .map((tenant) => tenant.id);
    const last_subscription_by_tenant = await fetchLastSubscriptions(lapsed_tenant_ids);

    const opportunity_rows = external_tenants
      .map((tenant) => {
        const opportunity_breakdown = computeOpportunity(
          tenant,
          modules_by_tier,
          last_subscription_by_tenant[tenant.id]
        );
        return {
          id: tenant.id,
          name: tenant.name || '—',
          active_subscriptions: tenant.metadata?.active_subscriptions || [],
          monthly_spend: tenant.metadata?.average_monthly_spend,
          contacts_count: tenant.metadata?.contacts_count,
          opportunity: opportunity_breakdown?.value ?? null,
          opportunity_breakdown,
          has_active_subscription: hasActiveSubscription(tenant),
          tenant
        };
      })
      .sort(
        (a, b) =>
          b.has_active_subscription - a.has_active_subscription ||
          (b.opportunity ?? -Infinity) - (a.opportunity ?? -Infinity)
      );

    setRows(opportunity_rows);
    setLoading(false);
  };

  const handleDataRequest = (page = 0, population = 10) => {
    setSearchKeyword('');
    setPageWindow({ page, population });
  };

  const handleSearchRequest = (keys, keyword = '', page = 0, population = 10) => {
    setSearchKeyword(keyword.trim().toLowerCase());
    setSearchMode(!!keyword.trim());
    setPageWindow({ page, population });
  };

  const handleItemClick = (row) => setSelectedRow(row);

  const matching_rows = search_keyword
    ? rows.filter((row) => row.name.toLowerCase().includes(search_keyword))
    : rows;
  const page_items = matching_rows.slice(
    page_window.page * page_window.population,
    (page_window.page + 1) * page_window.population
  );

  const config = {
    ...table_config,
    is_search_mode,
    items: page_items,
    total_count: matching_rows.length
  };

  const table = is_mobile_view ? (
    <MobileDatatable
      config={config}
      action={() => {}}
      onClick={handleItemClick}
      onListModeChange={setSearchMode}
      onDataRequest={handleDataRequest}
      onSearchRequest={handleSearchRequest}
    />
  ) : (
    <WebDataTable
      config={config}
      action={() => {}}
      onClick={handleItemClick}
      loading_data={loading}
      showHeader={false}
      onDataRequest={handleDataRequest}
      onSearchRequest={handleSearchRequest}
    />
  );

  return (
    <>
      {table}
      <GmModal
        title={selected_row?.name || 'Opportunity'}
        show_title
        show_modal={!!selected_row}
        onClose={() => setSelectedRow(null)}
      >
        {selected_row && <OpportunityDetail row={selected_row} />}
      </GmModal>
    </>
  );
};
